import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import { useEffect } from "react";

export interface SubsheetStatus {
  id: string;
  subsheet_id: string;
  status_id: string;
  created_at: string;
}

/**
 * Per-platform status configuration. Returns the rows linking a subsheet to its enabled statuses.
 * IMPORTANT: an EMPTY result means "default — all statuses enabled" (no config saved yet).
 * Rows present means only those status_ids are enabled for the platform.
 */
export const useSubsheetStatuses = (subsheetId: string | null) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!subsheetId) return;

    const channel = supabase
      .channel(`subsheet-statuses-${subsheetId}`)
      .on(
        "postgres_changes",
        {
          // No filter: events carry no row data to filter on. Any
          // subsheet_statuses change invalidates this subsheet's list — a
          // hair over-eager, harmless at this table's write rate.
          event: "*",
          schema: "public",
          table: "subsheet_statuses",
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["subsheet-statuses", subsheetId],
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [subsheetId, queryClient]);

  return useQuery<SubsheetStatus[]>({
    queryKey: ["subsheet-statuses", subsheetId],
    queryFn: async () => {
      if (!subsheetId) return [];

      const { data, error } = await supabase.from("subsheet_statuses").select("*").eq("subsheet_id", subsheetId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!subsheetId,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });
};

/**
 * Replaces one department's enabled-status set. `allEnabled` (every status
 * ticked) stores NO rows, which is what "every status" means. The one writer
 * both hooks below share.
 *
 * Adds before it removes: a department with no rows offers EVERY status, so
 * the old delete-then-insert left one wide open whenever the insert failed.
 * Now a failure part-way leaves its old list, or the old list plus the new
 * statuses — never nothing.
 */
async function writeSubsheetStatuses(subsheetId: string, statusIds: readonly string[], allEnabled: boolean) {
  if (allEnabled || statusIds.length === 0) {
    const { error } = await supabase.from("subsheet_statuses").delete().eq("subsheet_id", subsheetId);
    if (error) throw error;
    return;
  }
  const { error: insertError } = await supabase.from("subsheet_statuses").upsert(
    statusIds.map((status_id) => ({ subsheet_id: subsheetId, status_id })),
    { onConflict: "subsheet_id,status_id", ignoreDuplicates: true },
  );
  if (insertError) throw insertError;
  const { error: deleteError } = await supabase
    .from("subsheet_statuses")
    .delete()
    .eq("subsheet_id", subsheetId)
    .not("status_id", "in", `(${statusIds.join(",")})`);
  if (deleteError) throw deleteError;
}

/** Saves one department's statuses (Departments -> Manage statuses). */
export const useSetSubsheetStatuses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subsheetId,
      statusIds,
      allEnabled,
    }: {
      subsheetId: string;
      statusIds: string[];
      allEnabled: boolean;
    }) => writeSubsheetStatuses(subsheetId, statusIds, allEnabled),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["subsheet-statuses", variables.subsheetId],
      });
    },
  });
};

/**
 * Saves one status's departments from the Statuses page (#9304): the
 * per-department lists statusDepartmentChanges() worked out. Statuses do not
 * inherit, so departments are independent and written in parallel; every
 * department's list is refreshed once at the end.
 */
export const useSetStatusDepartments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (changes: ReadonlyArray<{ subsheetId: string; statusIds: string[]; allEnabled: boolean }>) => {
      await Promise.all(changes.map((c) => writeSubsheetStatuses(c.subsheetId, c.statusIds, c.allEnabled)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subsheet-statuses"] });
    },
  });
};
